// « Photo Vivante » in the album editor (owner, 2026-11-03): the client adds
// the video themself. Shown only for an album the option was sold for
// (worker GET /living/mine — no row, nothing here).
//   • a small reminder above the album while the video is missing;
//   • « Photo Vivante » in the menu (and the desktop toolbar) opens the sheet;
//   • the sheet: what it is in 2 lines, « Choisir ma vidéo », the progress,
//     then the printed last page with the video playing in it.
// Same upload flow as the team's order-page card (lib/livingUpload.ts).
// Once the album is sent to print the video can no longer change here (the
// still is printed on the last page) — the sheet says to write on WhatsApp.
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { livingApi } from '@/lib/api';
import { addLivingVideo, type LivingStep } from '@/lib/livingUpload';
import { waSupportLink } from '@/lib/whatsappSupport';

const GOLD = '#C9A04C';
const INK = '#2A211B';
const OPEN_EVENT = 'storiz:living-open';
const HIDE_KEY = 'storiz_living_nudge_hidden';

/** Opens the sheet from anywhere in the editor (menu row, toolbar button). */
export function openLivingPanel(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** This album's Photo Vivante row, or null when the option was not taken. */
export function useLivingAlbum(shopifyOrderId: string | undefined, albumIndex: number) {
  const q = useQuery({
    queryKey: ['living', 'mine', shopifyOrderId],
    queryFn: () => livingApi.mine(shopifyOrderId as string),
    enabled: !!shopifyOrderId,
    staleTime: 60_000,
    retry: false,
  });
  return q.data?.items.find((i) => i.album_index === albumIndex) ?? null;
}

export function LivingEditorPanel({ shopifyOrderId, albumIndex, locked }: { shopifyOrderId: string; albumIndex: number; locked: boolean }) {
  const qc = useQueryClient();
  const item = useLivingAlbum(shopifyOrderId, albumIndex);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<LivingStep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localVideo, setLocalVideo] = useState<string | null>(null);
  const [hidden, setHidden] = useState(() => { try { return sessionStorage.getItem(HIDE_KEY) === '1'; } catch { return false; } });
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, on);
    return () => window.removeEventListener(OPEN_EVENT, on);
  }, []);
  useEffect(() => () => { if (localVideo) URL.revokeObjectURL(localVideo); }, [localVideo]);

  if (!item) return null;
  // `draft` = her video arrived and our server is preparing it (1–3 min): for her it is done.
  const ready = item.status === 'ready' || item.status === 'draft';
  const busy = step !== null;

  async function run(file: File) {
    setError(null);
    try {
      await addLivingVideo(file, albumIndex, livingApi.client(shopifyOrderId), setStep);
      setLocalVideo(URL.createObjectURL(file));
      setStep(null);
      await qc.invalidateQueries({ queryKey: ['living', 'mine', shopifyOrderId] });
    } catch (e) {
      setStep(null);
      setError((e as Error).message || 'Ça n’a pas marché. Réessayez.');
    }
  }
  const hideNudge = () => { setHidden(true); try { sessionStorage.setItem(HIDE_KEY, '1'); } catch { /* private mode */ } };

  return (
    <>
      {!ready && !locked && !open && !hidden && (
        <div className="fixed left-1/2 -translate-x-1/2 z-[60] flex items-center gap-1 rounded-full shadow-lg pl-3.5 pr-1 py-1"
          style={{ bottom: 'calc(92px + env(safe-area-inset-bottom))', background: INK, color: '#FBF7F0' }} data-living-nudge>
          <button type="button" onClick={() => setOpen(true)} className="text-[13.5px] font-semibold py-1.5 whitespace-nowrap">
            🎬 Ajoutez la vidéo de votre Photo Vivante
          </button>
          <button type="button" onClick={hideNudge} aria-label="Masquer" className="w-8 h-8 rounded-full flex items-center justify-center text-[15px] opacity-70">×</button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" style={{ background: 'rgba(20,14,10,.45)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }} data-living-sheet>
          <div role="dialog" aria-modal="true" aria-labelledby="living-title"
            className="w-full sm:max-w-[420px] rounded-t-2xl sm:rounded-2xl p-5 pb-[calc(20px+env(safe-area-inset-bottom))] max-h-[92vh] overflow-y-auto"
            style={{ background: '#FFFDF9', colorScheme: 'light' }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="living-title" className="m-0 text-[19px] font-semibold" style={{ color: INK, fontFamily: "'Fraunces', Georgia, serif" }}>Photo Vivante</h2>
                <p className="m-0 mt-1 text-[14px] leading-relaxed text-[#6b5d50]">
                  La dernière page de l’album est une grande photo tirée de votre vidéo. Scannez le QR au dos de l’album : la photo prend vie.
                </p>
              </div>
              {!busy && <button type="button" onClick={() => setOpen(false)} className="text-[14px] font-semibold px-2 py-1 rounded-lg" style={{ color: INK }}>Fermer</button>}
            </div>

            {(ready || localVideo) && !busy && (
              <div className="mt-4 flex flex-col items-center gap-2">
                <div className="w-[62%] max-w-[230px] rounded-md overflow-hidden shadow-md border border-[#eadfce]" style={{ aspectRatio: '200 / 297', background: '#f4eee6' }}>
                  {localVideo
                    ? <video src={localVideo} poster={item.still_url ?? undefined} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                    : item.still_url && <img src={item.still_url} alt="Votre dernière page" className="w-full h-full object-cover" />}
                </div>
                <p className="m-0 text-[13.5px] font-semibold text-green-700">Prête ✓ Elle sera imprimée en dernière page.</p>
              </div>
            )}

            {!ready && !localVideo && !busy && !locked && (
              <ul className="mt-4 mb-0 pl-0 list-none space-y-1.5 text-[13.5px] text-[#4a3f36]">
                <li>📱 Une vidéo verticale, si possible</li>
                <li>⏱️ 10 à 30 secondes</li>
                <li>☀️ Bien éclairée : la plus belle image devient la photo imprimée</li>
              </ul>
            )}

            {busy && (
              <div className="mt-5" aria-live="polite">
                <p className="m-0 mb-2 text-[13.5px] text-[#4a3f36]">{step!.label}</p>
                <div className="h-2 rounded-full bg-[#efe7dc] overflow-hidden">
                  <div className="h-full transition-[width] duration-300" style={{ width: `${Math.round(step!.pct)}%`, background: GOLD }} />
                </div>
                <p className="m-0 mt-2 text-[12px] text-[#8a7a6b]">Gardez cette page ouverte jusqu’à la fin.</p>
              </div>
            )}

            {error && <p role="alert" className="m-0 mt-3 text-[13.5px] text-red-600">{error}</p>}

            {locked ? (
              <a href={waSupportLink('Bonjour, je voudrais changer la vidéo de ma Photo Vivante.')} target="_blank" rel="noreferrer"
                className="mt-5 block text-center rounded-full py-3.5 text-[15px] font-bold" style={{ background: GOLD, color: INK }}>
                Votre album est parti à l’impression · Écrivez-nous
              </a>
            ) : !busy && (
              <>
                <input ref={input} type="file" accept="video/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void run(f); }} />
                <button type="button" onClick={() => input.current?.click()}
                  className="mt-5 w-full rounded-full py-3.5 text-[15px] font-bold" style={{ background: ready || localVideo ? '#f2ebe1' : GOLD, color: INK }}>
                  {ready || localVideo ? 'Changer la vidéo' : 'Choisir ma vidéo'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
